import { useRef } from "react";
import { Layers, Loader2, CheckCircle2, XCircle, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MAX = 20;
const STATUS = {
  attente: ["En attente", "text-slate-500"], lecture: ["Lecture IA…", "text-sky-700"], prete: ["Prête à valider", "text-amber-700"],
  validee: ["Demande créée", "text-emerald-700"], erreur: ["Erreur", "text-red-600"], ignoree: ["Ignorée", "text-slate-400"],
};

export async function scanAll(files, setLot) {
  const items = files.slice(0, MAX).map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, preview: URL.createObjectURL(f), status: "attente", file: f }));
  if (files.length > MAX) toast.warning(`Maximum ${MAX} requêtes par lot : les suivantes sont ignorées`);
  setLot((l) => [...l, ...items]);
  const upd = (key, patch) => setLot((l) => l.map((x) => (x.key === key ? { ...x, ...patch } : x)));
  for (const it of items) {
    upd(it.key, { status: "lecture" });
    const fd = new FormData();
    fd.append("file", it.file);
    fd.append("lot", "1");
    try {
      const { data } = await api.post("/requetes/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      upd(it.key, { status: "prete", data, erreur: data.erreur || null, key: data.scan_id });
    } catch (e) { upd(it.key, { status: "erreur", erreur: formatApiError(e.response?.data?.detail) || "Lecture impossible" }); }
  }
}

export async function loadSaved(setLot) {
  const { data } = await api.get("/requetes/lot");
  if (!data.length) return;
  const items = await Promise.all(data.map(async (s) => {
    let preview = "";
    try { preview = URL.createObjectURL((await api.get(`/requetes/scan/${s.scan_id}/image`, { responseType: "blob" })).data); } catch { /* image indisponible */ }
    return { key: s.scan_id, name: `Requête du ${new Date(s.created_at).toLocaleDateString("fr-CA")}`, preview, status: "prete",
      data: { scan_id: s.scan_id, champs: s.champs, erreur: s.erreur }, erreur: s.erreur, saved: true };
  }));
  setLot((l) => [...l, ...items.filter((x) => !l.some((y) => y.key === x.key))]);
}

export const LotPanel = ({ lot, setLot, onValidate }) => {
  const input = useRef(null);
  const ignore = async (it) => {
    if (it.data?.scan_id) { try { await api.delete(`/requetes/scan/${it.data.scan_id}`); } catch { /* déjà retirée */ } }
    setLot((l) => l.map((x) => (x.key === it.key ? { ...x, status: "ignoree" } : x)));
  };
  const counts = lot.reduce((a, x) => ({ ...a, [x.status]: (a[x.status] || 0) + 1 }), {});
  return (
    <Card className="p-4 mb-4 space-y-3" data-testid="lot-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Layers className="h-5 w-5 text-sky-600" />
        <div className="font-semibold text-slate-900">Numérisation en lot</div>
        <span className="text-xs text-slate-500" data-testid="lot-counts">{lot.length} requête(s) · {counts.prete || 0} à valider · {counts.validee || 0} créée(s)</span>
        <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="lot-input"
               onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ""; if (f.length) scanAll(f, setLot); }} />
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => input.current?.click()} data-testid="lot-add-button">Ajouter des requêtes</Button>
        {lot.length > 0 && <Button size="sm" variant="ghost" onClick={() => setLot((l) => l.filter((x) => ["lecture", "attente", "prete"].includes(x.status)))} data-testid="lot-clear-button">Vider les terminées</Button>}
      </div>
      {lot.length === 0 && <div className="text-xs text-slate-500">Choisissez plusieurs photos ou scans de requêtes (jusqu'à {MAX}). L'IA les lit une à une; vous validez ensuite chaque demande. Le lot est sauvegardé : vous pouvez le reprendre plus tard ou depuis un autre poste avec votre compte.</div>}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {lot.map((it, i) => {
          const [label, cls] = STATUS[it.status];
          const c = it.data?.champs || {};
          return (
            <div key={it.key} className={`flex gap-2 rounded-lg border p-2 ${it.status === "ignoree" ? "opacity-50" : "border-slate-200"}`} data-testid={`lot-item-${i}`}>
              <img src={it.preview} alt="" className="h-16 w-12 rounded object-cover border" />
              <div className="min-w-0 flex-1 text-xs">
                <div className="truncate font-medium text-slate-800">{c.operation || c.sites?.A || it.name}</div>
                <div className={`flex items-center gap-1 ${cls}`} data-testid={`lot-status-${i}`}>
                  {it.status === "lecture" && <Loader2 className="h-3 w-3 animate-spin" />}
                  {it.status === "validee" && <CheckCircle2 className="h-3 w-3" />}
                  {it.status === "erreur" && <XCircle className="h-3 w-3" />} {label}
                </div>
                {it.erreur && <div className="text-red-600 truncate" title={it.erreur}>{it.erreur}</div>}
                {it.status === "prete" && (
                  <div className="mt-1 flex gap-1">
                    <Button size="sm" className="h-6 px-2 text-[11px]" style={{ background: "#0284C7" }} onClick={() => onValidate(i)} data-testid={`lot-validate-${i}`}><Eye className="h-3 w-3 mr-1" /> Valider</Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => ignore(it)} data-testid={`lot-ignore-${i}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
